import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@frontegg/react';
import './PublicStoryPage.css';
import { getLanguageName, isEnglish, isRTLLanguage } from '../utils/languages';
import { getArticlePublisherName } from '../features/CreateBrief/transformers';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function timeAgo(inputDate) {
    const now = new Date();
    const past = new Date(inputDate);
    const diffMs = now - past;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    return days === 1 ? '1 day ago' : `${days} days ago`;
}

// Third copy of this logic until now, and the one a client sees. It defers to
// the shared resolver so this page cannot name a publisher differently from
// the bucket screen or the Articles step.
function getPublisherName(article) {
    if (!article?.sources?.[0] && !article?.source && !article?.publisherName) return null;
    return getArticlePublisherName(article);
}


function getSentimentDisplay(articles) {
    if (!articles?.length) return null;
    let pos = 0, neg = 0, neu = 0;
    articles.forEach(a => {
        const s = a.sentiment;
        if (s === 'positive') pos++;
        else if (s === 'negative') neg++;
        else neu++;
    });
    if (pos >= neg && pos >= neu) return { emoji: '😊', label: 'Mostly Positive' };
    if (neg >= pos && neg >= neu) return { emoji: '😟', label: 'Mostly Negative' };
    return { emoji: '😐', label: 'Mostly Neutral' };
}

const PublicStoryPage = () => {
    const { slug } = useParams();
    const { isAuthenticated } = useAuth();
    const [story, setStory] = useState(null);
    const [articles, setArticles] = useState([]);
    const [excerpt, setExcerpt] = useState(null);
    const [fullExcerpt, setFullExcerpt] = useState(null);
    const [earliest, setEarliest] = useState(null);
    const [latest, setLatest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [excerptExpanded, setExcerptExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);

        fetch(`${BASE_URL}/public/story/${encodeURIComponent(slug)}`)
            .then(r => {
                if (r.status === 404) { setNotFound(true); setLoading(false); return null; }
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then(data => {
                if (cancelled || !data) return;
                setStory(data.story);
                setArticles(data.articles || []);
                setExcerpt(data.excerpt);
                setFullExcerpt(data.fullExcerpt);
                setEarliest(data.earliest);
                setLatest(data.latest);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Failed to load public story:', err);
                setNotFound(true);
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [slug]);

    useEffect(() => {
        if (story?.title) {
            document.title = `${story.title} | Alchemiq`;
        }
        return () => { document.title = 'Alchemiq'; };
    }, [story?.title]);

    useEffect(() => {
        let robotsMeta = document.querySelector('meta[name="robots"]');
        const createdRobots = !robotsMeta;
        if (!robotsMeta) {
            robotsMeta = document.createElement('meta');
            robotsMeta.setAttribute('name', 'robots');
            document.head.appendChild(robotsMeta);
        }
        robotsMeta.setAttribute('content', 'noindex, nofollow');
        return () => { if (createdRobots) robotsMeta.remove(); };
    }, []);

    useEffect(() => {
        if (!story) return;
        const ogImage = `${window.location.origin}/textOnlyLogoOrr-01.png`;
        const description = excerpt?.text || `Story with ${articles.length} source articles`;
        const metas = {
            'og:title': story.title,
            'og:description': description,
            'og:image': ogImage,
            'og:url': window.location.href,
            'og:type': 'article',
            'og:site_name': 'Alchemiq',
            'twitter:card': 'summary',
            'twitter:title': story.title,
            'twitter:description': description,
            'twitter:image': ogImage,
        };
        const cleanup = [];
        Object.entries(metas).forEach(([property, content]) => {
            const attr = property.startsWith('twitter:') ? 'name' : 'property';
            let el = document.querySelector(`meta[${attr}="${property}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, property);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
            if (created) cleanup.push(el);
        });
        return () => cleanup.forEach(el => el.remove());
    }, [story, excerpt, articles.length]);

    const isRTL = story?.dominantLanguage && isRTLLanguage(story.dominantLanguage);

    if (loading) {
        return (
            <div className="psp">
                <header className="psp-header">
                    <div className="psp-header-inner">
                        <a href="https://app.alchemiqai.com" className="psp-logo-link">
                            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psp-logo" />
                        </a>
                    </div>
                </header>
                <main className="psp-main">
                    <div className="psp-loading">
                        <div className="psp-loading-spinner" />
                        <span>Loading story…</span>
                    </div>
                </main>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="psp">
                <header className="psp-header">
                    <div className="psp-header-inner">
                        <a href="https://app.alchemiqai.com" className="psp-logo-link">
                            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psp-logo" />
                        </a>
                    </div>
                </header>
                <main className="psp-main">
                    <div className="psp-not-found">
                        <h1>Story not found</h1>
                        <p>This story may have been removed or the link may be incorrect.</p>
                        <a href="https://app.alchemiqai.com" className="psp-cta-btn">Go to Alchemiq</a>
                    </div>
                </main>
            </div>
        );
    }

    const sentiment = getSentimentDisplay(articles);
    const descriptionText = excerpt?.text || story.subject || `A developing story covered by ${articles.length} news sources.`;

    return (
        <div className="psp" dir={isRTL ? 'rtl' : 'ltr'}>
            <header className="psp-header">
                <div className="psp-header-inner">
                    <a href="https://app.alchemiqai.com" className="psp-logo-link">
                        <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psp-logo" />
                    </a>
                </div>
            </header>

            <div className="psp-sticky-top">
                <h1 className="psp-story-title">
                    <a href={`${window.location.origin}/story/${slug}`}>{story.title}</a>
                </h1>

                <div className="psp-meta">
                    <svg className="psp-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <span className="psp-meta-badge">{articles.length} {articles.length === 1 ? 'source' : 'sources'}</span>
                    {earliest && (
                        <>
                            <span className="psp-meta-dot">•</span>
                            <span className="psp-meta-text">First reported {timeAgo(earliest)}</span>
                        </>
                    )}
                    {latest && earliest && new Date(latest).getTime() !== new Date(earliest).getTime() && (
                        <>
                            <span className="psp-meta-dot">•</span>
                            <span className="psp-meta-text">Latest {timeAgo(latest)}</span>
                        </>
                    )}
                    {sentiment && (
                        <>
                            <span className="psp-meta-dot">•</span>
                            <span className="psp-meta-sentiment">{sentiment.emoji} {sentiment.label}</span>
                        </>
                    )}
                </div>

                {descriptionText && (
                    <div className={`psp-excerpt ${excerptExpanded ? 'psp-excerpt--open' : ''}`}>
                        <div className="psp-excerpt-inner">
                            <p className="psp-excerpt-text">
                                {excerptExpanded && fullExcerpt?.text ? fullExcerpt.text : descriptionText}
                            </p>
                        </div>
                        {fullExcerpt?.text && fullExcerpt.text !== descriptionText && (
                            <button
                                className="psp-excerpt-toggle"
                                onClick={() => setExcerptExpanded(prev => !prev)}
                            >
                                {excerptExpanded ? 'Show less ▲' : 'Read more ▼'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <main className="psp-scroll-area">
                <div className="psp-scroll-content">
                    <h2 className="psp-sources-heading">
                        <a href={`${window.location.origin}/story/${slug}`}>
                            Based on {articles.length} source {articles.length === 1 ? 'article' : 'articles'}
                        </a>
                    </h2>

                    <div className="psp-sources-list">
                        {articles.map((article, index) => {
                            const publisher = getPublisherName(article);
                            const headline = article.subject || article.title || 'Untitled';
                            const isNonEnglish = article.lang && !isEnglish(article.lang);

                            return (
                                <a
                                    key={article._id}
                                    href={article.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="psp-source-item"
                                >
                                    <div className="psp-source-index">{index + 1}</div>
                                    <div className="psp-source-content">
                                        <div className="psp-source-headline">{headline}</div>
                                        <div className="psp-source-meta">
                                            {publisher && (
                                                <span className="psp-source-publisher">
                                                    {article.isPreferredPublisher && <span className="psp-preferred-star">★</span>}
                                                    {publisher}
                                                </span>
                                            )}
                                            {article.dateTimePub && (
                                                <span className="psp-source-time">{timeAgo(article.dateTimePub)}</span>
                                            )}
                                            {isNonEnglish && (
                                                <span className="psp-source-lang">{getLanguageName(article.lang)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="psp-source-arrow">↗</span>
                                </a>
                            );
                        })}
                    </div>

                </div>
            </main>
               <footer className="psp-footer--logged-in">
                    <div className="psp-footer-copy">Alchemiq - News that drives growth © {new Date().getFullYear()} </div>
                </footer>
        </div>
    );
};

export default PublicStoryPage;
